import { useRef } from "react";
import { Microphone, FileAudio, VideoConference } from "@phosphor-icons/react";
import { inputCls, labelCls } from "@/lib/ui";

const Option = ({ active, onClick, icon: Icon, title, sub, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left p-4 border transition-colors ${active ? "border-[var(--o-fg)] bg-[var(--o-surface)]" : "border-[var(--o-border)] hover:border-[var(--o-border-strong)]"}`}
    data-testid={testid}
  >
    <Icon size={20} weight={active ? "fill" : "regular"} />
    <div className="mt-3 text-sm font-semibold">{title}</div>
    <div className="text-xs text-[var(--o-muted2)] mt-1">{sub}</div>
  </button>
);

const PrepStatus = ({ prep }) => {
  if (!prep || prep.status === "idle") return null;
  if (prep.status === "extracting") {
    return (
      <div className="space-y-2" data-testid="extract-progress">
        <div className="flex justify-between font-mono text-[11px] text-[var(--o-muted)]">
          <span>Extracting audio from {prep.originalName} in the browser…</span>
          <span>{Math.round(prep.progress * 100)}%</span>
        </div>
        <div className="h-1 bg-[var(--o-border)]"><div className="h-full bg-[var(--o-fg)] transition-[width] duration-300" style={{ width: `${prep.progress * 100}%` }} /></div>
      </div>
    );
  }
  if (prep.status === "done") {
    return <p className="font-mono text-[11px] text-[#10B981]" data-testid="extract-done">Audio extracted · {(prep.originalSize / 1e6).toFixed(0)} MB → {(prep.size / 1e6).toFixed(1)} MB in {prep.seconds.toFixed(0)}s</p>;
  }
  return <p className="font-mono text-[11px] text-[#F59E0B]" data-testid="extract-failed">Could not extract in browser ({prep.error}). The original file will be uploaded instead.</p>;
};

export const OnlineHelp = () => (
  <ol className="text-xs text-[var(--o-muted2)] space-y-1 list-decimal pl-4">
    <li>Join the call in Chrome first. When you press Start, Chrome asks what to share; nobody in the call sees this.</li>
    <li><b className="text-[var(--o-fg)]">Google Meet:</b> pick <i>Chrome tab</i> → your Meet tab → turn on <i>Also share tab audio</i>.</li>
    <li><b className="text-[var(--o-fg)]">Zoom / Teams app:</b> pick <i>Entire screen</i> → turn on <i>Also share system audio</i>.</li>
    <li>Use headphones, so your mic only hears you.</li>
  </ol>
);

export const SourcePicker = ({ mode, setMode, file, onPick, prep, speed, setSpeed, selfName, setSelfName }) => {
  const ref = useRef(null);
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-3 gap-2">
        <Option active={mode === "live"} onClick={() => setMode("live")} icon={Microphone} title="In the room" sub="Laptop mic hears everyone, 45s chunks" testid="source-live-option" />
        <Option active={mode === "online"} onClick={() => setMode("online")} icon={VideoConference} title="Online meeting" sub="Your mic + the call's audio, as Me / Them" testid="source-online-option" />
        <Option active={mode === "replay"} onClick={() => setMode("replay")} icon={FileAudio} title="Upload recording" sub="Replay a past meeting to tune prompts" testid="source-replay-option" />
      </div>
      {mode === "online" && (
        <div className="border border-[var(--o-border)] p-4 space-y-4 rise" data-testid="online-options">
          <label className="space-y-2 block max-w-xs">
            <span className={labelCls}>Your name in the call</span>
            <input autoComplete="off" className={inputCls} placeholder="Me" value={selfName || ""} onChange={(e) => setSelfName(e.target.value)} data-testid="online-self-name-input" />
          </label>
          <OnlineHelp />
        </div>
      )}
      {mode === "replay" && (
        <div className="border border-[var(--o-border)] p-4 space-y-4 rise" data-testid="replay-options">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => ref.current?.click()} className="border border-[var(--o-border)] px-3 py-2 text-sm hover:bg-[var(--o-surface)] transition-colors" data-testid="replay-choose-file-button">
              Choose recording
            </button>
            <span className="font-mono text-xs text-[var(--o-muted)] truncate" data-testid="replay-file-name">
              {file ? `${file.name} · ${(file.size / 1e6).toFixed(1)} MB` : "No file selected"}
            </span>
            <input ref={ref} type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.ogg,.webm,.mp4,.mov,.mkv" className="hidden" onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="replay-file-input" />
          </div>
          <PrepStatus prep={prep} />
          <div className="flex gap-2">
            {[["fast", "As fast as Gemini returns"], ["realtime", "Real time"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setSpeed(v)} className={`px-3 py-1.5 text-xs font-mono border transition-colors ${speed === v ? "border-[var(--o-fg)] text-[var(--o-fg)]" : "border-[var(--o-border)] text-[var(--o-muted2)] hover:text-[var(--o-fg)]"}`} data-testid={`replay-speed-${v}`}>
                {l}
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--o-muted2)]">Flag interval is measured in recording time. Video files (e.g. Meet .mp4) have their audio pulled out in the browser first, so only a small audio file is uploaded. The server deletes it after chunking.</p>
        </div>
      )}
    </div>
  );
};
